/**
 * AiBadge + ReferencesPanel
 *
 * Two reusable bits for surfacing AI-sourced fields on any contact /
 * company view. Reads the new `field_provenance` map written by the
 * backend's enrichment endpoint.
 *
 *   { fieldName: { source: 'ai' | 'human',
 *                  reference_url?: string,
 *                  fetched_at?: string } }
 *
 * AiBadge — small purple chip with a Sparkles icon. Render next to
 *   any label that maps to a field where `provenance[field].source ===
 *   'ai'`. Hover/title shows the reference URL.
 *
 * ReferencesPanel — a collapsible disclosure section that lists every
 *   AI-sourced field as a KVP "Field name → reference URL". Closed by
 *   default; expand with a ▶ disclosure triangle.
 */
import { ReactNode, useState } from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';

export interface FieldProvenance {
    source: 'ai' | 'human';
    reference_url?: string | null;
    fetched_at?: string | null;
}

export type ProvenanceMap = Record<string, FieldProvenance | undefined>;

interface AiBadgeProps {
    /** Provenance entry for the field this badge sits next to. The
     *  component renders nothing if the field is human-sourced (or
     *  the entry is missing) — callers can safely conditionally
     *  pass `provenance[field]` without wrapping it in a check. */
    entry?: FieldProvenance | null;
    /** Optional override for the tooltip title. Defaults to the
     *  reference URL or "AI-generated". */
    title?: string;
    /** Optional click handler — pop the references panel, focus a
     *  link, whatever. */
    onClick?: () => void;
    className?: string;
}

export function AiBadge({ entry, title, onClick, className = '' }: AiBadgeProps) {
    if (!entry || entry.source !== 'ai') return null;
    const tooltip = title
        || (entry.reference_url && entry.reference_url !== 'ai_inferred'
            ? `AI · source: ${entry.reference_url}`
            : 'AI-generated · no public source cited');
    return (
        <span
            title={tooltip}
            onClick={onClick}
            className={[
                'inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5',
                'text-[10px] font-semibold uppercase tracking-wide text-violet-700',
                'ring-1 ring-violet-200',
                onClick ? 'cursor-pointer hover:bg-violet-100' : '',
                className,
            ].join(' ')}
        >
            <Sparkles size={10} />
            AI
        </span>
    );
}


interface ReferencesPanelProps {
    provenance?: ProvenanceMap | null;
    /** Optional human-friendly labels — falls back to the raw field
     *  key (snake_case) when missing. */
    fieldLabels?: Record<string, string>;
    /** Override default heading. */
    title?: string;
    /** Force-open on first render (default: collapsed). */
    defaultOpen?: boolean;
    className?: string;
}

/**
 * Renders the disclosure-triangle section with one KVP row per
 * AI-sourced field. Hidden entirely when there are no AI fields to
 * cite so it doesn't clutter the page on hand-entered records.
 */
export function ReferencesPanel({
    provenance,
    fieldLabels = {},
    title = 'References',
    defaultOpen = false,
    className = '',
}: ReferencesPanelProps) {
    const [open, setOpen] = useState(defaultOpen);

    const aiEntries = Object.entries(provenance || {})
        .filter(([, v]) => v && v.source === 'ai') as Array<[string, FieldProvenance]>;

    if (aiEntries.length === 0) return null;

    return (
        <section className={[
            'rounded-xl border border-gray-100 bg-white',
            className,
        ].join(' ')}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                aria-expanded={open}
            >
                <ChevronRight
                    size={14}
                    className={`text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
                />
                <span>{title}</span>
                <span className="ml-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200">
                    {aiEntries.length}
                </span>
                <span className="ml-auto text-xs text-gray-400">
                    Where AI-sourced values came from
                </span>
            </button>
            {open && (
                <ul className="divide-y divide-gray-100 px-4 pb-3 pt-1 text-sm">
                    {aiEntries.map(([field, entry]) => (
                        <li key={field} className="flex items-baseline gap-2 py-1.5">
                            <span className="shrink-0 font-medium text-gray-700">
                                {fieldLabels[field] || prettyFieldName(field)}:
                            </span>
                            <ReferenceLink url={entry.reference_url} />
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}


function ReferenceLink({ url }: { url: string | null | undefined }): ReactNode {
    if (!url || url === 'ai_inferred') {
        return (
            <span className="text-xs italic text-gray-400">
                AI inferred · no public URL
            </span>
        );
    }
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-xs text-primary-dark underline underline-offset-2 hover:text-primary-darker"
        >
            {url}
        </a>
    );
}


function prettyFieldName(snake: string): string {
    return snake
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}


export default AiBadge;
